<template>
  <app-content :title="'事实类型管理'">
    <div class="rft-page">
      <!-- 说明 -->
      <div class="rf-notice">
        <i class="h-icon-info"></i>
        <span>管理参考事实的字段结构定义。账套级类型可自由创建、编辑和删除；全局类型只读，可克隆到账套后再修改。</span>
      </div>

      <!-- 工具栏 -->
      <div class="rft-toolbar">
        <div class="rft-filters">
          <select v-model="filterSource" class="form-input rft-filter-select">
            <option value="">全部来源</option>
            <option value="ACCOUNT">账套级</option>
            <option value="GLOBAL">全局</option>
          </select>
          <select v-model="filterStatus" class="form-input rft-filter-select">
            <option value="">全部状态</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="ARCHIVED">ARCHIVED</option>
          </select>
          <input
            v-model="filterKeyword"
            type="text"
            class="form-input rft-filter-search"
            placeholder="按 code / 名称搜索"
          />
        </div>
        <div class="rft-toolbar-actions">
          <button class="btn btn-default btn-sm" @click="loadList" :disabled="loading">
            <i class="h-icon-refresh"></i> 刷新
          </button>
          <button class="btn btn-primary" @click="openCreateDialog">
            <i class="h-icon-plus"></i> 新建类型
          </button>
        </div>
      </div>

      <!-- 列表 -->
      <div class="rft-table-wrapper">
        <div v-if="loading" class="rft-loading">加载中...</div>
        <div v-else-if="filteredList.length === 0" class="rft-empty">
          <i class="h-icon-search"></i>
          <span>暂无匹配的事实类型</span>
        </div>
        <table v-else class="rf-table rft-table">
          <colgroup>
            <col style="width:72px">
            <col style="width:200px">
            <col style="width:160px">
            <col style="width:70px">
            <col style="width:130px">
            <col style="width:55px">
            <col style="width:auto">
          </colgroup>
          <thead>
            <tr>
              <th>来源</th>
              <th>Code</th>
              <th>名称</th>
              <th>状态</th>
              <th>业务域</th>
              <th class="rf-num">字段数</th>
              <th class="rf-nowrap rf-th-actions">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in filteredList" :key="item.factTypeCode">
              <td>
                <span class="rft-source-tag" :class="item.source === 'GLOBAL' ? 'rft-source-global' : 'rft-source-account'">
                  {{ item.source === 'GLOBAL' ? '全局' : '账套级' }}
                </span>
              </td>
              <td class="rft-code-cell" :title="item.factTypeCode">{{ item.factTypeCode }}</td>
              <td :title="item.name">{{ item.name || '-' }}</td>
              <td>
                <span class="rf-badge" :class="item.status === 'ACTIVE' ? 'st-active' : 'st-archived'">
                  {{ item.status }}
                </span>
              </td>
              <td :title="item.businessDomain">{{ item.businessDomain || '-' }}</td>
              <td class="rf-num">{{ item.fieldCount != null ? item.fieldCount : '-' }}</td>
              <td class="rf-actions">
                <!-- 账套级 ACTIVE -->
                <template v-if="item.source === 'ACCOUNT' && item.status === 'ACTIVE'">
                  <button class="rf-act-btn rf-act-edit" @click="openEditDialog(item)">编辑</button>
                  <button class="rf-act-btn rft-act-archive" @click="doArchive(item)">归档</button>
                  <button class="rf-act-btn rft-act-delete" @click="doDelete(item, false)">删除</button>
                </template>
                <!-- 账套级 ARCHIVED -->
                <template v-else-if="item.source === 'ACCOUNT' && item.status === 'ARCHIVED'">
                  <button class="rf-act-btn rft-act-restore" @click="doRestore(item)">还原</button>
                  <button class="rf-act-btn rft-act-delete" @click="doDelete(item, false)">删除</button>
                </template>
                <!-- 全局（只读） -->
                <template v-else-if="item.source === 'GLOBAL'">
                  <button class="rf-act-btn rft-act-clone" @click="openCloneDialog(item)">复制到账套</button>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ======== 新建 / 编辑 弹层 ======== -->
      <div v-if="dialog.open" ref="dialogOverlay" class="rf-drawer-overlay" @click.self="closeDialog">
        <div class="rft-dialog">
          <div class="rf-drawer-header">
            <h3>{{ dialog.isEdit ? '编辑事实类型' : '新建事实类型' }}</h3>
            <p v-if="dialog.isEdit" class="rft-dialog-subtitle">Code 只读；元数据和字段分别保存</p>
            <button class="rf-drawer-close" @click="closeDialog"><i class="h-icon-close"></i></button>
          </div>
          <div class="rf-drawer-body">

            <!-- 元数据 -->
            <div class="rft-section-title">基本信息</div>
            <div class="rf-form-row">
              <label><span class="required">*</span> Code：</label>
              <input
                v-model="dialog.form.factTypeCode"
                type="text"
                class="form-input"
                :disabled="dialog.isEdit"
                placeholder="大写字母开头，3-64位，如 MY_CUSTOM_TYPE"
              />
              <span v-if="dialog.errors.factTypeCode" class="rft-field-error">{{ dialog.errors.factTypeCode }}</span>
            </div>
            <div class="rf-form-row">
              <label><span class="required">*</span> 名称：</label>
              <input v-model="dialog.form.name" type="text" class="form-input" placeholder="中文或英文可读名称" />
              <span v-if="dialog.errors.name" class="rft-field-error">{{ dialog.errors.name }}</span>
            </div>
            <div class="rf-form-row">
              <label>业务域：</label>
              <input v-model="dialog.form.businessDomain" type="text" class="form-input" placeholder="如 PURCHASE / LOGISTICS（可留空）" />
            </div>
            <div class="rf-form-row">
              <label>适用事件类型：</label>
              <textarea
                v-model="dialog.form.applicableEventTypesRaw"
                class="rft-textarea"
                placeholder="逗号分隔或 JSON 数组，如：PAYMENT,REFUND 或 [&quot;PAYMENT&quot;,&quot;REFUND&quot;]（可留空）"
                rows="2"
              ></textarea>
            </div>

            <!-- 字段编辑器 -->
            <div class="rft-section-title" style="margin-top:18px">
              字段定义
              <button class="btn btn-default btn-xs" style="margin-left:12px" @click="addField">+ 添加字段</button>
            </div>
            <div v-if="dialog.errors.fields" class="rft-global-error">{{ dialog.errors.fields }}</div>
            <div class="rft-fields-wrapper">
              <table class="rf-table rft-fields-table">
                <colgroup>
                  <col style="width:130px">
                  <col style="width:130px">
                  <col style="width:130px">
                  <col style="width:80px">
                  <col style="width:130px">
                  <col style="width:46px">
                  <col style="width:56px">
                  <col style="width:52px">
                  <col style="width:42px">
                </colgroup>
                <thead>
                  <tr>
                    <th><span class="required">*</span> fieldCode</th>
                    <th><span class="required">*</span> fieldName</th>
                    <th>sourceHeader（Excel表头）</th>
                    <th><span class="required">*</span> dataType</th>
                    <th><span class="required">*</span> semanticRole</th>
                    <th>必填</th>
                    <th>可匹配</th>
                    <th>sort</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="dialog.fields.length === 0">
                    <td colspan="9" class="rft-empty-row">暂无字段，请点击「添加字段」</td>
                  </tr>
                  <tr v-for="(f, fi) in dialog.fields" :key="fi" :class="{ 'rft-field-row-error': fieldHasError(fi) }">
                    <td>
                      <input v-model="f.fieldCode" type="text" class="form-input rft-inline-input" placeholder="camelCase 或 snake_case" />
                    </td>
                    <td>
                      <input v-model="f.fieldName" type="text" class="form-input rft-inline-input" placeholder="显示名称" />
                    </td>
                    <td>
                      <input v-model="f.sourceHeader" type="text" class="form-input rft-inline-input" placeholder="Excel 列标题" />
                    </td>
                    <td>
                      <select v-model="f.dataType" class="form-input rft-inline-select">
                        <option value="">选择</option>
                        <option value="STRING">STRING</option>
                        <option value="DECIMAL">DECIMAL</option>
                        <option value="DATE">DATE</option>
                        <option value="INT">INT</option>
                      </select>
                    </td>
                    <td>
                      <select v-model="f.semanticRole" class="form-input rft-inline-select">
                        <option value="">选择</option>
                        <option v-for="role in semanticRoles" :key="role" :value="role">{{ role }}</option>
                      </select>
                    </td>
                    <td class="rft-center">
                      <input type="checkbox" v-model="f.required" />
                    </td>
                    <td class="rft-center">
                      <input type="checkbox" v-model="f.matchable" />
                    </td>
                    <td>
                      <input v-model.number="f.sort" type="number" class="form-input rft-sort-input" min="0" />
                    </td>
                    <td>
                      <button class="rft-del-field-btn" @click="removeField(fi)" title="删除该字段">×</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="dialog.fieldErrors && dialog.fieldErrors.length" class="rft-field-errors-list">
              <div v-for="(e, ei) in dialog.fieldErrors" :key="ei" class="rft-field-error-item">{{ e }}</div>
            </div>

            <!-- 操作按钮 -->
            <div class="rf-form-actions" style="margin-top:18px">
              <button
                class="btn btn-primary"
                @click="submitDialog"
                :disabled="dialog.submitting"
              >{{ dialog.submitting ? '保存中...' : (dialog.isEdit ? '保存' : '新建') }}</button>
              <button class="btn btn-default" @click="closeDialog">取消</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ======== 克隆确认弹层 ======== -->
      <div v-if="cloneDialog.open" class="rf-drawer-overlay" @click.self="closeCloneDialog">
        <div class="rft-small-dialog">
          <div class="rf-drawer-header">
            <h3>克隆到当前账套</h3>
            <button class="rf-drawer-close" @click="closeCloneDialog"><i class="h-icon-close"></i></button>
          </div>
          <div class="rf-drawer-body">
            <p style="margin-bottom:12px;color:#606266;font-size:13px">
              将全局模板 <strong>{{ cloneDialog.factTypeCode }}</strong> 克隆为账套级类型，可填写新 Code（默认与模板相同）。
            </p>
            <div class="rf-form-row">
              <label>新 Code：</label>
              <input
                v-model="cloneDialog.newCode"
                type="text"
                class="form-input"
                placeholder="留空则使用原 Code"
              />
            </div>
            <div class="rf-form-actions" style="margin-top:14px">
              <button class="btn btn-primary" @click="doClone" :disabled="cloneDialog.submitting">
                {{ cloneDialog.submitting ? '克隆中...' : '确认克隆' }}
              </button>
              <button class="btn btn-default" @click="closeCloneDialog">取消</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ======== 强制确认弹层（有引用数据时） ======== -->
      <div v-if="forceDialog.open" class="rf-drawer-overlay" @click.self="forceDialog.open = false">
        <div class="rft-small-dialog">
          <div class="rf-drawer-header">
            <h3>确认强制操作</h3>
            <button class="rf-drawer-close" @click="forceDialog.open = false"><i class="h-icon-close"></i></button>
          </div>
          <div class="rf-drawer-body">
            <div class="rft-warn-block">
              <i class="h-icon-warning"></i>
              <span>{{ forceDialog.message }}</span>
            </div>
            <p style="margin-top:10px;color:#606266;font-size:13px">该类型下存在已导入的参考事实数据。强制操作将继续执行，历史数据不受影响，但后续导入将无法再使用此类型结构。</p>
            <div class="rf-form-actions" style="margin-top:14px">
              <button class="btn btn-danger" @click="forceDialog.onConfirm" :disabled="forceDialog.submitting">
                {{ forceDialog.submitting ? '执行中...' : '强制确认' }}
              </button>
              <button class="btn btn-default" @click="forceDialog.open = false">取消</button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </app-content>
</template>

<script>
const FACT_TYPE_CODE_RE = /^[A-Z][A-Z0-9_]{2,63}$/;
const FIELD_CODE_RE = /^[a-zA-Z][a-zA-Z0-9_]{1,63}$/;

const STRUCTURAL_ROLES = ['PERIOD', 'ENTITY', 'CURRENCY'];

// 错误码 → 中文映射
const ERROR_CODE_CN = {
  REFERENCE_FACT_TYPE_GLOBAL_TEMPLATE_READONLY: '全局模板只读，请使用「复制到账套」功能',
  REFERENCE_FACT_TYPE_NOT_FOUND: '事实类型不存在',
  REFERENCE_FACT_TYPE_CODE_DUPLICATE: '该 Code 在当前账套内已存在',
  REFERENCE_FACT_TYPE_CODE_CONFLICTS_GLOBAL_TEMPLATE: '该 Code 与全局 ACTIVE 模板冲突，请使用「复制到账套」功能',
  REFERENCE_FACT_TYPE_FIELD_CODE_DUPLICATE: '字段 Code 重复',
  REFERENCE_FACT_TYPE_INVALID_SEMANTIC_ROLE: '语义角色值不合法',
  REFERENCE_FACT_TYPE_STRUCTURAL_ROLE_DUPLICATE: '结构性角色（PERIOD / ENTITY / CURRENCY）在同一类型中最多各出现一次',
  REFERENCE_FACT_TYPE_NO_MATCHABLE_FIELD: '至少需要一个 matchable 字段',
  REFERENCE_FACT_TYPE_FIELD_HAS_DATA: '该类型下已有引用数据，需确认后强制执行',
  REFERENCE_FACT_TYPE_INVALID_FACT_TYPE_CODE: 'factTypeCode 格式不合法（需大写字母开头，3-64位，仅含大写字母/数字/下划线）',
  REFERENCE_FACT_TYPE_INVALID_FIELD_CODE: 'fieldCode 格式不合法（需字母开头，2-64位，仅含字母/数字/下划线）',
  REFERENCE_FACT_TYPE_INVALID_DATA_TYPE: 'dataType 值不合法'
};

function mapErrorMsg(msg) {
  if (!msg) return '操作失败';
  for (const [code, cn] of Object.entries(ERROR_CODE_CN)) {
    if (msg.includes(code)) return cn;
  }
  return msg;
}

function emptyField(sort) {
  return {
    fieldCode: '',
    fieldName: '',
    sourceHeader: '',
    dataType: '',
    semanticRole: '',
    required: false,
    matchable: false,
    sort: sort || 0
  };
}

export default {
  name: 'ReferenceFactTypeManager',
  data() {
    return {
      loading: false,
      list: [],
      semanticRoles: [],

      filterSource: '',
      filterStatus: '',
      filterKeyword: '',

      dialog: {
        open: false,
        isEdit: false,
        submitting: false,
        form: {
          factTypeCode: '',
          name: '',
          businessDomain: '',
          applicableEventTypesRaw: ''
        },
        fields: [],
        errors: {},
        fieldErrors: []
      },

      cloneDialog: {
        open: false,
        factTypeCode: '',
        newCode: '',
        submitting: false
      },

      forceDialog: {
        open: false,
        message: '',
        submitting: false,
        onConfirm: null
      }
    };
  },

  computed: {
    filteredList() {
      let items = this.list;
      if (this.filterSource) {
        items = items.filter(i => i.source === this.filterSource);
      }
      if (this.filterStatus) {
        items = items.filter(i => i.status === this.filterStatus);
      } else {
        // 默认：账套级显示全部状态，全局只显示 ACTIVE
        items = items.filter(i => i.source === 'ACCOUNT' || i.status === 'ACTIVE');
      }
      if (this.filterKeyword) {
        const kw = this.filterKeyword.toLowerCase();
        items = items.filter(i =>
          (i.factTypeCode || '').toLowerCase().includes(kw) ||
          (i.name || '').toLowerCase().includes(kw)
        );
      }
      return items;
    }
  },

  created() {
    this.loadList();
    this.loadSemanticRoles();
  },

  mounted() {
    this._docClick = () => {};
    document.addEventListener('click', this._docClick);
  },

  beforeDestroy() {
    if (this._docClick) document.removeEventListener('click', this._docClick);
  },

  methods: {
    async loadList() {
      this.loading = true;
      try {
        const r = await this.$api.referenceFactType.list();
        if (r && r.code === 200) {
          const items = (r.data && r.data.items) || [];
          this.list = items.map(item => ({
            ...item,
            fieldCount: item.fieldCount != null ? item.fieldCount : (item.fields ? item.fields.length : null)
          }));
        } else {
          this.$Message.error((r && r.msg) ? mapErrorMsg(r.msg) : '加载失败');
        }
      } catch (e) {
        this.$Message.error('加载事实类型失败');
      } finally {
        this.loading = false;
      }
    },

    async loadSemanticRoles() {
      try {
        const r = await this.$api.referenceFactType.getSemanticRoles();
        if (r && r.code === 200) {
          const data = r.data || {};
          const values = Array.isArray(data.values) ? [...data.values] : [];
          values.sort();
          this.semanticRoles = values;
        }
      } catch (e) {
        // 降级：使用已知枚举
        this.semanticRoles = [
          'ADJUSTMENT', 'AMOUNT', 'COMPENSATION', 'COUNTERPARTY', 'CURRENCY',
          'DEDUCTION', 'ENDING_BALANCE', 'ENTITY', 'EXCESS', 'EXPLAINABLE',
          'GENERIC', 'INCREASE', 'OPENING_BALANCE', 'ORDER_NO', 'PAYMENT',
          'PERIOD', 'PLATFORM', 'REFUND', 'STORE'
        ];
      }
    },

    // ---- 新建 ----
    openCreateDialog() {
      this.dialog = {
        open: true,
        isEdit: false,
        submitting: false,
        form: { factTypeCode: '', name: '', businessDomain: '', applicableEventTypesRaw: '' },
        fields: [emptyField(0)],
        errors: {},
        fieldErrors: []
      };
      this._mountDialogToBody();
    },

    // ---- 编辑 ----
    async openEditDialog(item) {
      this.dialog = {
        open: true,
        isEdit: true,
        submitting: false,
        form: {
          factTypeCode: item.factTypeCode,
          name: item.name || '',
          businessDomain: item.businessDomain || '',
          applicableEventTypesRaw: (item.applicableEventTypes && item.applicableEventTypes !== '[]') ? item.applicableEventTypes : ''
        },
        fields: [],
        errors: {},
        fieldErrors: []
      };
      this._mountDialogToBody();
      // 加载字段
      try {
        const r = await this.$api.referenceFactType.detail(item.factTypeCode);
        if (r && r.code === 200) {
          const data = r.data || {};
          this.dialog.fields = (data.fields || []).map(f => ({
            id: f.id,
            fieldCode: f.fieldCode || '',
            fieldName: f.fieldName || '',
            sourceHeader: f.sourceHeader || '',
            dataType: f.dataType || '',
            semanticRole: f.semanticRole || '',
            required: !!f.required,
            matchable: !!f.matchable,
            sort: f.sort || 0
          }));
        }
      } catch (e) {
        this.$Message.error('加载字段详情失败');
      }
    },

    _mountDialogToBody() {
      this.$nextTick(() => {
        const el = this.$refs.dialogOverlay;
        if (el && el.parentNode !== document.body) {
          document.body.appendChild(el);
        }
      });
    },

    closeDialog() {
      const el = this.$refs.dialogOverlay;
      if (el && el.parentNode) el.parentNode.removeChild(el);
      this.dialog.open = false;
    },

    addField() {
      const nextSort = this.dialog.fields.length;
      this.dialog.fields.push(emptyField(nextSort));
    },

    removeField(index) {
      this.dialog.fields.splice(index, 1);
    },

    fieldHasError(fi) {
      return !!(this.dialog.fieldErrors && this.dialog.fieldErrors.some(e => e.includes(`字段${fi + 1}`)));
    },

    // ---- 客户端预校验 ----
    validateDialog() {
      const errors = {};
      const fieldErrors = [];

      const { factTypeCode, name } = this.dialog.form;

      if (!this.dialog.isEdit) {
        if (!factTypeCode) {
          errors.factTypeCode = '必填';
        } else if (!FACT_TYPE_CODE_RE.test(factTypeCode)) {
          errors.factTypeCode = '格式不合法：大写字母开头，3-64 位，仅含大写字母/数字/下划线';
        }
      }
      if (!name) errors.name = '必填';

      const fields = this.dialog.fields;

      if (fields.length === 0) {
        errors.fields = '至少需要一个字段';
      } else {
        // 字段逐行校验
        const fieldCodes = new Set();
        const structuralUsed = {};

        fields.forEach((f, fi) => {
          const prefix = `字段${fi + 1}(${f.fieldCode || '?'})`;
          if (!f.fieldCode) {
            fieldErrors.push(`${prefix}：fieldCode 必填`);
          } else if (!FIELD_CODE_RE.test(f.fieldCode)) {
            fieldErrors.push(`${prefix}：fieldCode 格式不合法（字母开头，2-64位）`);
          } else if (fieldCodes.has(f.fieldCode)) {
            fieldErrors.push(`${prefix}：fieldCode 重复`);
          } else {
            fieldCodes.add(f.fieldCode);
          }
          if (!f.fieldName) fieldErrors.push(`${prefix}：fieldName 必填`);
          if (!f.dataType) fieldErrors.push(`${prefix}：dataType 必选`);
          if (!f.semanticRole) fieldErrors.push(`${prefix}：semanticRole 必选`);

          if (STRUCTURAL_ROLES.includes(f.semanticRole)) {
            if (structuralUsed[f.semanticRole]) {
              fieldErrors.push(`${prefix}：结构性角色 ${f.semanticRole} 已被另一字段使用，同一类型内最多出现一次`);
            } else {
              structuralUsed[f.semanticRole] = true;
            }
          }
        });

        const hasMatchable = fields.some(f => f.matchable);
        if (!hasMatchable) {
          errors.fields = '至少需要一个 matchable 字段';
        }
      }

      this.dialog.errors = errors;
      this.dialog.fieldErrors = fieldErrors;
      return Object.keys(errors).length === 0 && fieldErrors.length === 0;
    },

    parseApplicableEventTypes(raw) {
      // 空 textarea → 显式 "[]"（区别于"未提交此字段 = 不更新"语义）
      if (!raw || !raw.trim()) return '[]';
      const trimmed = raw.trim();
      // 尝试 JSON 数组
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return JSON.stringify(parsed);
        return JSON.stringify([String(parsed)]);
      } catch (e) {
        // 逗号分隔
        const arr = trimmed.split(',').map(s => s.trim()).filter(Boolean);
        return JSON.stringify(arr);
      }
    },

    async submitDialog() {
      if (!this.validateDialog()) return;
      this.dialog.submitting = true;
      try {
        await (this.dialog.isEdit ? this._submitEdit() : this._submitCreate());
      } finally {
        this.dialog.submitting = false;
      }
    },

    async _submitCreate() {
      const { factTypeCode, name, businessDomain, applicableEventTypesRaw } = this.dialog.form;
      const body = {
        factTypeCode,
        name,
        fields: this._buildFieldsPayload()
      };
      if (businessDomain) body.businessDomain = businessDomain;
      // 始终发送 applicableEventTypes（parseApplicableEventTypes 永远返回字符串）
      body.applicableEventTypes = this.parseApplicableEventTypes(applicableEventTypesRaw);

      try {
        const r = await this.$api.referenceFactType.create(body);
        if (r && r.code === 200) {
          this.$Message.success('新建成功');
          this.closeDialog();
          await this.loadList();
        } else {
          this.$Message.error(mapErrorMsg(r && r.msg));
        }
      } catch (e) {
        const msg = (e && e.msg) ? mapErrorMsg(e.msg) : '新建失败';
        this.$Message.error(msg);
      }
    },

    async _submitEdit() {
      const { factTypeCode, name, businessDomain, applicableEventTypesRaw } = this.dialog.form;
      // 1. 保存元数据
      const metaBody = { name };
      if (businessDomain !== undefined) metaBody.businessDomain = businessDomain;
      // 始终发送 applicableEventTypes，允许用户清空到 "[]"
      metaBody.applicableEventTypes = this.parseApplicableEventTypes(applicableEventTypesRaw);

      try {
        const r1 = await this.$api.referenceFactType.updateType(factTypeCode, metaBody);
        if (!r1 || r1.code !== 200) {
          this.$Message.error(mapErrorMsg(r1 && r1.msg));
          return;
        }
      } catch (e) {
        this.$Message.error((e && e.msg) ? mapErrorMsg(e.msg) : '保存元数据失败');
        return;
      }

      // 2. 保存字段
      await this._saveFieldsWithForceCheck(factTypeCode, false);
    },

    async _saveFieldsWithForceCheck(factTypeCode, force) {
      const fields = this._buildFieldsPayload();
      try {
        const r2 = await this.$api.referenceFactType.saveFields(factTypeCode, fields, force);
        if (r2 && r2.code === 200) {
          this.$Message.success('保存成功');
          this.closeDialog();
          await this.loadList();
        } else {
          const msg = r2 && r2.msg;
          if (msg && msg.includes('REFERENCE_FACT_TYPE_FIELD_HAS_DATA')) {
            this._openForceDialog(
              '该类型下存在已导入的参考事实数据，修改字段结构将影响历史数据解析。',
              async () => {
                this.forceDialog.submitting = true;
                try {
                  await this._saveFieldsWithForceCheck(factTypeCode, true);
                  this.forceDialog.open = false;
                } finally {
                  this.forceDialog.submitting = false;
                }
              }
            );
          } else {
            this.$Message.error(mapErrorMsg(msg));
          }
        }
      } catch (e) {
        const msg = (e && e.msg) || '';
        if (msg.includes('REFERENCE_FACT_TYPE_FIELD_HAS_DATA')) {
          this._openForceDialog(
            '该类型下存在已导入的参考事实数据，修改字段结构将影响历史数据解析。',
            async () => {
              this.forceDialog.submitting = true;
              try {
                await this._saveFieldsWithForceCheck(factTypeCode, true);
                this.forceDialog.open = false;
              } finally {
                this.forceDialog.submitting = false;
              }
            }
          );
        } else {
          this.$Message.error(mapErrorMsg(msg) || '保存字段失败');
        }
      }
    },

    _buildFieldsPayload() {
      return this.dialog.fields.map(f => {
        const obj = {
          fieldCode: f.fieldCode,
          fieldName: f.fieldName,
          dataType: f.dataType,
          semanticRole: f.semanticRole,
          required: !!f.required,
          matchable: !!f.matchable,
          sort: f.sort || 0
        };
        if (f.sourceHeader) obj.sourceHeader = f.sourceHeader;
        return obj;
      });
    },

    // ---- 归档 ----
    async doArchive(item) {
      if (!confirm(`确认归档「${item.name || item.factTypeCode}」？归档后不可新增导入，但历史数据保留。`)) return;
      try {
        const r = await this.$api.referenceFactType.archive(item.factTypeCode);
        if (r && r.code === 200) {
          this.$Message.success('已归档');
          await this.loadList();
        } else {
          this.$Message.error(mapErrorMsg(r && r.msg));
        }
      } catch (e) {
        this.$Message.error(mapErrorMsg(e && e.msg) || '归档失败');
      }
    },

    // ---- 还原 ----
    async doRestore(item) {
      if (!confirm(`确认还原「${item.name || item.factTypeCode}」为 ACTIVE？`)) return;
      try {
        const r = await this.$api.referenceFactType.restore(item.factTypeCode);
        if (r && r.code === 200) {
          this.$Message.success('已还原');
          await this.loadList();
        } else {
          this.$Message.error(mapErrorMsg(r && r.msg));
        }
      } catch (e) {
        this.$Message.error(mapErrorMsg(e && e.msg) || '还原失败');
      }
    },

    // ---- 删除 ----
    async doDelete(item, force) {
      if (!force && !confirm(`确认删除「${item.name || item.factTypeCode}」？此操作不可撤销。`)) return;
      try {
        const r = await this.$api.referenceFactType.remove(item.factTypeCode, force);
        if (r && r.code === 200) {
          this.$Message.success('已删除');
          await this.loadList();
        } else {
          const msg = r && r.msg;
          if (msg && msg.includes('REFERENCE_FACT_TYPE_FIELD_HAS_DATA')) {
            this._openForceDialog(
              `「${item.name || item.factTypeCode}」下存在已导入的参考事实数据，强制删除后历史数据将失去类型定义关联。`,
              async () => {
                this.forceDialog.submitting = true;
                try {
                  await this.doDelete(item, true);
                  this.forceDialog.open = false;
                } finally {
                  this.forceDialog.submitting = false;
                }
              }
            );
          } else {
            this.$Message.error(mapErrorMsg(msg));
          }
        }
      } catch (e) {
        const msg = (e && e.msg) || '';
        if (msg.includes('REFERENCE_FACT_TYPE_FIELD_HAS_DATA')) {
          this._openForceDialog(
            `「${item.name || item.factTypeCode}」下存在已导入的参考事实数据，强制删除后历史数据将失去类型定义关联。`,
            async () => {
              this.forceDialog.submitting = true;
              try {
                await this.doDelete(item, true);
                this.forceDialog.open = false;
              } finally {
                this.forceDialog.submitting = false;
              }
            }
          );
        } else {
          this.$Message.error(mapErrorMsg(msg) || '删除失败');
        }
      }
    },

    // ---- 克隆弹层 ----
    openCloneDialog(item) {
      this.cloneDialog = {
        open: true,
        factTypeCode: item.factTypeCode,
        newCode: item.factTypeCode,
        submitting: false
      };
    },

    closeCloneDialog() {
      this.cloneDialog.open = false;
    },

    async doClone() {
      const { factTypeCode, newCode } = this.cloneDialog;
      this.cloneDialog.submitting = true;
      try {
        const r = await this.$api.referenceFactType.cloneFromTemplate(factTypeCode, newCode);
        if (r && r.code === 200) {
          this.$Message.success('克隆成功，已创建账套级副本');
          this.closeCloneDialog();
          await this.loadList();
        } else {
          this.$Message.error(mapErrorMsg(r && r.msg));
        }
      } catch (e) {
        this.$Message.error(mapErrorMsg(e && e.msg) || '克隆失败');
      } finally {
        this.cloneDialog.submitting = false;
      }
    },

    // ---- 强制确认弹层 ----
    _openForceDialog(message, onConfirm) {
      this.forceDialog = {
        open: true,
        message,
        submitting: false,
        onConfirm
      };
    }
  }
};
</script>

<style scoped>
/* ===== 共享 rf-* 基础样式 =====
   这些类名与 ReferenceFactImport.vue 共用，但两边均为 <style scoped>，
   scoped 样式不跨组件生效，故本组件必须自带一份定义，否则列表、表单、
   弹层(.rf-drawer-overlay 无 position:fixed) 全部失去样式 —— 表现为
   「新建类型」点击后弹层以无样式裸块追加到 body 底部，肉眼看不到=没反应。 */
.rf-notice { display: flex; align-items: flex-start; gap: 10px; padding: 12px 16px; background: #f0f7ff; border: 1px solid #b3d8ff; border-radius: 4px; margin-bottom: 20px; font-size: 13px; color: #409eff; line-height: 1.6; }
.rf-notice i { margin-top: 2px; }

.required { color: #f56c6c; }
.form-input { padding: 6px 10px; border: 1px solid #dcdfe6; border-radius: 4px; font-size: 13px; }
.form-input:focus { outline: none; border-color: #409eff; }

.rf-form-row { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; font-size: 13px; }
.rf-form-row label { min-width: 90px; color: #606266; text-align: right; }
.rf-form-row .form-input { flex: 1; min-width: 180px; }
.rf-form-actions { display: flex; gap: 8px; align-items: center; }

/* table */
.rf-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.rf-table th { padding: 8px 6px; background: #f5f7fa; border-bottom: 1px solid #ebeef5; font-weight: 500; color: #606266; font-size: 12px; vertical-align: middle; text-align: left; }
.rf-table td { padding: 7px 6px; border-bottom: 1px solid #ebeef5; color: #303133; vertical-align: middle; }
.rf-table tr:hover td { background: #f5f7fa; }
.rf-nowrap { white-space: nowrap; }
.rf-num { text-align: right; font-variant-numeric: tabular-nums; }
.rf-th-actions { text-align: right; }
.rf-actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; white-space: nowrap; }
.rf-act-btn { padding: 1px 6px; border: 1px solid transparent; border-radius: 3px; background: none; font-size: 12px; cursor: pointer; }
.rf-act-edit { color: #409eff; border-color: #409eff; }
.rf-act-edit:hover { background: #ecf5ff; }

/* badge 形状（颜色由 .st-active / .st-archived 提供） */
.rf-badge { display: inline-block; font-size: 12px; padding: 2px 10px; border-radius: 10px; line-height: 18px; white-space: nowrap; font-weight: 500; border: 1px solid transparent; }

/* 弹层遮罩 + 弹层头/体（居中模态，区别于 Import 页的右侧抽屉） */
.rf-drawer-overlay { position: fixed; inset: 0; z-index: 2100; background: rgba(15,23,42,0.46); display: flex; align-items: center; justify-content: center; }
.rf-drawer-header { position: relative; padding: 20px 24px 12px; border-bottom: 1px solid #ebeef5; }
.rf-drawer-header h3 { margin: 0; font-size: 16px; color: #303133; }
.rf-drawer-header p { margin: 4px 0 0; font-size: 12px; color: #909399; }
.rf-drawer-close { position: absolute; top: 16px; right: 16px; background: none; border: none; font-size: 18px; cursor: pointer; color: #909399; }
.rf-drawer-body { padding: 16px 24px; overflow-y: auto; }

.rft-page {
  padding: 0 0 40px;
}

.rft-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 14px 0 10px;
  flex-wrap: wrap;
  gap: 8px;
}

.rft-filters {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.rft-filter-select {
  width: 110px;
}

.rft-filter-search {
  width: 200px;
}

.rft-toolbar-actions {
  display: flex;
  gap: 8px;
}

.rft-table-wrapper {
  overflow-x: auto;
}

.rft-table {
  min-width: 700px;
}

.rft-loading,
.rft-empty {
  padding: 40px 0;
  text-align: center;
  color: #909399;
  font-size: 14px;
}

.rft-empty i {
  margin-right: 6px;
}

.rft-source-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.rft-source-account {
  background: #e8f4fd;
  color: #1890ff;
}

.rft-source-global {
  background: #f0f0f0;
  color: #666;
}

.rft-code-cell {
  font-family: monospace;
  font-size: 12px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rft-act-archive {
  color: #e6a23c;
  border-color: #e6a23c;
}
.rft-act-archive:hover {
  background: #fdf6ec;
}

.rft-act-restore {
  color: #67c23a;
  border-color: #67c23a;
}
.rft-act-restore:hover {
  background: #f0f9eb;
}

.rft-act-delete {
  color: #f56c6c;
  border-color: #f56c6c;
}
.rft-act-delete:hover {
  background: #fef0f0;
}

.rft-act-clone {
  color: #409eff;
  border-color: #409eff;
}
.rft-act-clone:hover {
  background: #ecf5ff;
}

/* Dialog */
.rft-dialog {
  background: #fff;
  border-radius: 6px;
  width: 900px;
  max-width: 96vw;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0,0,0,.15);
}

.rft-small-dialog {
  background: #fff;
  border-radius: 6px;
  width: 480px;
  max-width: 96vw;
  box-shadow: 0 4px 20px rgba(0,0,0,.15);
}

.rft-dialog-subtitle {
  margin: 2px 0 0;
  color: #909399;
  font-size: 12px;
}

.rft-section-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  margin: 10px 0 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  align-items: center;
}

.rft-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 13px;
  resize: vertical;
  font-family: monospace;
  color: #303133;
}
.rft-textarea:focus {
  outline: none;
  border-color: #409eff;
}

.rft-fields-wrapper {
  overflow-x: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.rft-fields-table {
  min-width: 820px;
  border: none;
}

.rft-fields-table th,
.rft-fields-table td {
  padding: 5px 6px;
  font-size: 12px;
}

.rft-inline-input {
  width: 100%;
  min-width: 80px;
  padding: 3px 6px;
  font-size: 12px;
  box-sizing: border-box;
}

.rft-inline-select {
  width: 100%;
  padding: 3px 4px;
  font-size: 12px;
  box-sizing: border-box;
}

.rft-sort-input {
  width: 50px;
  padding: 3px 6px;
  font-size: 12px;
  text-align: center;
}

.rft-center {
  text-align: center;
}

.rft-del-field-btn {
  background: none;
  border: none;
  color: #f56c6c;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 2px 6px;
}
.rft-del-field-btn:hover {
  color: #c0392b;
}

.rft-field-row-error {
  background: #fff5f5;
}

.rft-field-errors-list {
  margin-top: 8px;
  padding: 8px 10px;
  background: #fff5f5;
  border: 1px solid #fbc4c4;
  border-radius: 4px;
}

.rft-field-error-item {
  font-size: 12px;
  color: #f56c6c;
  margin-bottom: 2px;
}

.rft-field-error {
  margin-left: 8px;
  font-size: 12px;
  color: #f56c6c;
}

.rft-global-error {
  color: #f56c6c;
  font-size: 12px;
  margin: 4px 0 6px;
}

.rft-empty-row {
  text-align: center;
  color: #909399;
  padding: 16px 0;
  font-size: 13px;
}

.rft-warn-block {
  background: #fdf6ec;
  border: 1px solid #f5dab1;
  border-radius: 4px;
  padding: 10px 12px;
  color: #e6a23c;
  font-size: 13px;
}

.rft-warn-block i {
  margin-right: 6px;
}

/* 复用 rf-* 基础样式的补充 — 覆盖 app-content 内部 rf-badge 颜色 */
.st-active {
  background: #e8f8f0;
  color: #27ae60;
  border-color: #b7e4c7;
}
.st-archived {
  background: #f0f0f0;
  color: #888;
  border-color: #d5d5d5;
}
</style>
